import type { RawApiResponse, RawPaginatedApiResponse } from '@/types'
import { getProjectId, getUserDisplayName, getUserId, logger, tokenHolder } from '@/utils'
import { tokenStorage } from '@/utils'
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import axios from 'axios'

/**
 * 自定义 API 客户端类型
 * 响应拦截器已解包数据，所以返回 Promise<T> 而不是 Promise<AxiosResponse<T>>
 */
export interface ApiClient extends Omit<AxiosInstance, 'get' | 'post' | 'put' | 'delete' | 'patch'> {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
  post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>
  put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
  patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>
}

// ========================= 缓存实现 =========================

/**
 * 缓存条目
 */
interface CacheEntry {
  data: unknown
  timestamp: number
  etag?: string
}

/**
 * 可缓存的请求配置（扩展 Axios config，避免 as any）
 */
interface CacheableConfig extends InternalAxiosRequestConfig {
  __cached?: CacheEntry
  _retry?: boolean
}

interface RefreshResponse {
  token: string
  expiresIn: number
  refreshToken?: string | null
}

class RequestCache {
  private cache = new Map<string, CacheEntry>()
  private readonly ttl = 30000 // 30秒缓存

  private getCacheKey(config: InternalAxiosRequestConfig): string {
    const { method, url, params } = config
    return `${method}:${url}:${JSON.stringify(params || {})}`
  }

  get(config: InternalAxiosRequestConfig): CacheEntry | null {
    // 只缓存 GET 请求
    if (config.method?.toUpperCase() !== 'GET') {
      return null
    }

    const key = this.getCacheKey(config)
    const entry = this.cache.get(key)

    if (!entry) {
      return null
    }

    // 检查是否过期
    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key)
      return null
    }

    return entry
  }

  set(config: InternalAxiosRequestConfig, data: unknown, etag?: string): void {
    if (config.method?.toUpperCase() !== 'GET') {
      return
    }

    const key = this.getCacheKey(config)
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      etag
    })
  }

  clear(): void {
    this.cache.clear()
  }

  /**
   * 根据 URL 前缀清除缓存
   */
  clearByPrefix(urlPrefix: string): void {
    for (const [key] of this.cache) {
      if (key.includes(urlPrefix)) {
        this.cache.delete(key)
      }
    }
  }
}

const requestCache = new RequestCache()
let pendingRefreshPromise: Promise<string | null> | null = null

// ========================= 类型守卫 =========================

/**
 * 判断响应数据是否为标准 ApiResponse 格式（含 code 字段）
 */
function isApiResponse(data: unknown): data is RawApiResponse {
  return typeof data === 'object' && data !== null && 'code' in data
}

/**
 * 判断响应数据是否为分页响应（含 total + page 字段）
 */
function isPaginatedResponse(data: unknown): data is RawPaginatedApiResponse {
  return isApiResponse(data) && 'total' in data && 'page' in data
}

/**
 * 判断业务码是否表示成功
 */
function isSuccessCode(code: number): boolean {
  return code === 200 || code === 0
}

// ========================= Axios 实例 =========================

/**
 * 创建 Axios 实例
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 开发环境：baseURL 指向 agent(8001) 时，/api/v1/datalake/* 不会经 Vite 转发到 datalake(8012)，埋点/统一目录会超时或失败
if (import.meta.env.DEV) {
  const devBase = String(import.meta.env.VITE_API_BASE_URL || '/api/v1')
  if (/localhost:8001|127\.0\.0\.1:8001/.test(devBase)) {
    logger.warn(
      '[api] VITE_API_BASE_URL 指向 agent(8001)。访问 /api/v1/datalake/* 需走 Vite 相对路径 /api/v1（见 web-app/.env.development），否则请求无法到达 datalake-service(8012)。',
    )
  }
}

function isAuthEndpoint(url?: string): boolean {
  if (!url) return false
  return url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/refresh')
}

function toSafeHeaderValue(raw: string): string {
  // 浏览器 XHR 仅允许 ISO-8859-1 头值；统一 URL 编码为 ASCII，服务端按需解码。
  return encodeURIComponent(raw)
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = tokenHolder.getRefreshToken()
  if (!refreshToken) {
    return null
  }

  try {
    const response = await axios.post<RefreshResponse>(
      `${apiClient.defaults.baseURL}/auth/refresh`,
      { refreshToken },
      {
        timeout: apiClient.defaults.timeout,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )

    const nextAccessToken = response.data.token
    const nextRefreshToken = response.data.refreshToken ?? undefined
    tokenHolder.setTokens(nextAccessToken, nextRefreshToken)
    await tokenStorage.saveTokens(nextAccessToken, nextRefreshToken)
    return nextAccessToken
  } catch (error) {
    tokenHolder.clearTokens()
    tokenStorage.clearTokens()
    try {
      localStorage.removeItem('userId')
    } catch {
      // 忽略 localStorage 不可用场景
    }
    logger.error('Token refresh failed:', error)
    return null
  }
}

// ========================= 请求拦截器 =========================

/**
 * 请求拦截器（添加缓存 + 认证 + 项目信息）
 */
apiClient.interceptors.request.use(
    (config) => {
      const cacheableConfig = config as CacheableConfig

      // 检查缓存
      const cached = requestCache.get(config)
      if (cached) {
        logger.debug(`[缓存命中] ${config.method?.toUpperCase()} ${config.url}`)
        // 添加 If-None-Match 头，支持 ETag 验证
        if (cached.etag) {
          config.headers['If-None-Match'] = cached.etag
        }
        // 将缓存数据附加到 config，供响应拦截器使用
        cacheableConfig.__cached = cached
      }

      // 添加 token 等认证信息（从内存中的 tokenHolder 获取，而非 localStorage）
      const token = tokenHolder.getAccessToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      // 添加项目ID和用户ID (知识库管理API需要)
      if (!config.headers['X-Project-Id']) {
        config.headers['X-Project-Id'] = getProjectId()
      }
      if (!config.headers['X-User-Id']) {
        config.headers['X-User-Id'] = getUserId()
      }
      const displayName = getUserDisplayName()
      if (displayName && !isAuthEndpoint(config.url)) {
        config.headers['X-Username'] = toSafeHeaderValue(displayName)
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
)

// ========================= 响应拦截器 =========================

/**
 * 从 ApiResponse 中解包数据
 * - 分页响应：返回 { data, total, page, page_size }
 * - 普通响应：返回 data 部分
 */
function unwrapApiResponse(data: RawApiResponse): unknown {
  if (isPaginatedResponse(data)) {
    return {
      data: data.data,
      total: data.total,
      page: data.page,
      page_size: data.page_size
    }
  }
  return data.data
}

/**
 * 响应拦截器（添加缓存支持）
 * 将 ApiResponse<T> 解包为 T，简化调用方的使用
 *
 * 处理逻辑：
 * 1. 304 Not Modified → 返回缓存数据
 * 2. 标准 ApiResponse 格式（有 code 字段）→ 解包 data
 * 3. 分页响应（有 total + page 字段）→ 返回 { data, total, page, page_size }
 * 4. 非标准格式（无 code 字段）→ 直接返回
 */
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      const { data, config, status, headers } = response
      const cacheableConfig = config as CacheableConfig

      // 处理 304 Not Modified（缓存仍然有效）
      if (status === 304 && cacheableConfig.__cached) {
        logger.debug(`[304 缓存有效] ${config.method?.toUpperCase()} ${config.url}`)
        return cacheableConfig.__cached.data
      }

      // 判断是否是标准 ApiResponse 格式
      if (!isApiResponse(data)) {
        // 后端直接返回数据对象（没有 code 包装），直接返回
        return data
      }

      // 检查业务码
      if (!isSuccessCode(data.code)) {
        return Promise.reject(new Error(data.message || '请求失败'))
      }

      // 解包响应数据
      const finalData = unwrapApiResponse(data)

      // 对特定接口尝试缓存（GET 200 且为历史相关，排除会话列表）
      // 注意：/sessions 列表不缓存，因为会话运行态（last_trace_status）变化频繁，
      // 缓存会导致列表显示过期状态（如已失败的会话仍显示为已完成）
      if (status === 200 && config.method?.toUpperCase() === 'GET') {
        const isSessionsList = config.url?.includes('/sessions') && !config.url?.includes('/sessions/')
        const shouldCache = !isSessionsList && config.url?.includes('/history')
        if (shouldCache && finalData !== undefined) {
          const etag = headers['etag']
          requestCache.set(config, finalData, etag)
          logger.debug(`[缓存存储] ${config.method?.toUpperCase()} ${config.url}`)
        }
      }

      return finalData
    },
    async (error: AxiosError) => {
      // AbortController / 路由切换等主动取消，不记为 API 错误
      if (axios.isCancel(error)) {
        return Promise.reject(error)
      }

      const originalConfig = error.config as CacheableConfig | undefined
      const statusCode = error.response?.status

      if (
        statusCode === 401 &&
        originalConfig &&
        !originalConfig._retry &&
        !isAuthEndpoint(originalConfig.url) &&
        tokenHolder.getRefreshToken()
      ) {
        originalConfig._retry = true
        pendingRefreshPromise = pendingRefreshPromise ?? refreshAccessToken().finally(() => {
          pendingRefreshPromise = null
        })

        const nextAccessToken = await pendingRefreshPromise
        if (nextAccessToken) {
          originalConfig.headers = originalConfig.headers ?? {}
          originalConfig.headers.Authorization = `Bearer ${nextAccessToken}`
          return apiClient.request(originalConfig)
        }

        // 刷新失败：清除认证状态并重定向到登录页
        try {
          const { useAuthStore } = await import('@/stores/auth')
          const authStore = useAuthStore()
          authStore.logout()
        } catch {
          // auth store 不可用时，已在 refreshAccessToken 中清理了 token
        }
        window.location.href = '/login'
        return Promise.reject(error)
      }

      // 处理 HTTP 错误
      logger.error('API Error:', error)
      return Promise.reject(error)
    }
)

// ========================= 导出 =========================

/** 缓存管理工具 */
export const cacheManager = {
  clear: () => requestCache.clear(),
  clearSessions: () => requestCache.clearByPrefix('/sessions')
}

// 类型断言：响应拦截器已解包数据，返回类型应为 T 而非 AxiosResponse<T>
export default apiClient as unknown as ApiClient
